// frontend/src/features/seo/components/GovernanceSettingsModal.js
import React, { useState } from 'react';

export default function GovernanceSettingsModal({
  isOpen,
  onClose,
  initialSettings,
  onSaveSettings,
}) {
  const [settings, setSettings] = useState(() => ({
    ...(initialSettings || {}),
    cmsProvider: initialSettings?.cmsProvider || 'WordPress',
  }));
  const [savedSuccess, setSavedSuccess] = useState(false);

  React.useEffect(() => {
    if (initialSettings) {
      setSettings({
        ...initialSettings,
        cmsProvider: initialSettings.cmsProvider || 'WordPress',
      });
    }
  }, [initialSettings, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    const toSave = {
      ...settings,
      cmsProvider: settings.cmsProvider || 'WordPress',
    };
    onSaveSettings && onSaveSettings(toSave);
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
      onClose();
    }, 1200);
  };

  return (
    <div className="seo-modal-overlay" onClick={onClose}>
      <div className="seo-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="seo-modal-header">
          <div>
            <h3 className="seo-modal-title">
              <i className="fas fa-shield-halved text-purple"></i> Programmatic Publishing
              Governance
            </h3>
          </div>
          <button type="button" className="seo-modal-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="seo-modal-body">
          <p className="seo-modal-intro">
            Enforce velocity limits, semantic duplicate thresholds, and CMS staging safeguards.
            <br />
            <strong>Rule:</strong> The platform prohibits autonomous or unapproved mass publishing.
          </p>

          <div className="seo-gov-group">
            <label className="seo-gov-label">
              <span>Daily Publishing Velocity Cap</span>
              <span className="seo-gov-badge">{settings.dailyPublishCap} articles / day</span>
            </label>
            <p className="seo-gov-hint">
              Restricts maximum programmatic drafts published within a rolling 24-hour window to
              protect domain reputation.
            </p>
            <input
              type="range"
              min="1"
              max="20"
              className="seo-range-slider"
              value={settings.dailyPublishCap}
              onChange={(e) =>
                setSettings({ ...settings, dailyPublishCap: Number(e.target.value) })
              }
            />
          </div>

          <div className="seo-gov-group">
            <label className="seo-gov-label">
              <span>Minimum Uniqueness Threshold</span>
              <span className="seo-gov-badge">
                {settings.minSimilarityThreshold}% max similarity
              </span>
            </label>
            <p className="seo-gov-hint">
              Automatically flags and rejects programmatic drafts if semantic cosine similarity with
              existing pages exceeds this threshold.
            </p>
            <input
              type="range"
              min="50"
              max="95"
              step="5"
              className="seo-range-slider"
              value={settings.minSimilarityThreshold}
              onChange={(e) =>
                setSettings({ ...settings, minSimilarityThreshold: Number(e.target.value) })
              }
            />
          </div>

          <div className="seo-gov-toggle-row">
            <div>
              <span className="seo-gov-toggle-title">Mandatory Editorial Approval</span>
              <p className="seo-gov-hint">
                Require human editor review before any brief or article is pushed to CMS
              </p>
            </div>
            <input
              type="checkbox"
              className="seo-gov-checkbox"
              checked={settings.requireEditorialApproval}
              onChange={(e) =>
                setSettings({ ...settings, requireEditorialApproval: e.target.checked })
              }
            />
          </div>

          <div className="seo-gov-toggle-row">
            <div>
              <span className="seo-gov-toggle-title">Stage as Draft First</span>
              <p className="seo-gov-hint">
                Deploy initially to CMS staging/draft state with instant rollback support
              </p>
            </div>
            <input
              type="checkbox"
              className="seo-gov-checkbox"
              checked={settings.autoStageToCms}
              onChange={(e) => setSettings({ ...settings, autoStageToCms: e.target.checked })}
            />
          </div>

          <div className="seo-cms-connection-box">
            <div className="seo-cms-header">
              <span>
                Connected CMS: <strong>{settings.cmsProvider || 'Not configured'}</strong>
              </span>
              <span
                className={`seo-badge-tag ${
                  settings.connectionStatus === 'connected' ? 'green' : 'gray'
                }`}
              >
                <i className="fas fa-circle-dot"></i> {settings.connectionStatus || 'disconnected'}
              </span>
            </div>

            <div className="seo-gov-group" style={{ marginTop: '12px', marginBottom: '8px' }}>
              <label className="seo-gov-label" style={{ fontSize: '12px' }}>
                <span>CMS Provider</span>
              </label>
              <select
                className="seo-gov-select"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                }}
                value={settings.cmsProvider || 'WordPress'}
                onChange={(e) => setSettings({ ...settings, cmsProvider: e.target.value })}
                data-testid="gov-cms-provider-select"
              >
                <option value="WordPress">WordPress (REST API)</option>
                <option value="Webflow">Webflow</option>
                <option value="Ghost">Ghost</option>
                <option value="Generic Headless">Generic Headless CMS</option>
              </select>
            </div>

            <div className="seo-gov-group" style={{ marginTop: '8px', marginBottom: '0' }}>
              <label className="seo-gov-label" style={{ fontSize: '12px' }}>
                <span>CMS API Endpoint / Webhook URL</span>
              </label>
              <input
                type="text"
                className="seo-gov-input"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
                placeholder="https://yourdomain.com/wp-json/ or https://yourdomain.com"
                value={settings.cmsEndpoint || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    cmsEndpoint: e.target.value,
                    connectionStatus: e.target.value ? 'connected' : 'disconnected',
                  })
                }
                data-testid="gov-cms-endpoint-input"
              />
            </div>

            <div className="seo-gov-group" style={{ marginTop: '10px', marginBottom: '0' }}>
              <label className="seo-gov-label" style={{ fontSize: '12px' }}>
                <span>Application Password / API Key (Required for Live CMS)</span>
              </label>
              <input
                type="password"
                className="seo-gov-input"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
                placeholder="username:xxxx xxxx xxxx xxxx or Bearer token"
                value={settings.cmsApiKey || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    cmsApiKey: e.target.value,
                  })
                }
                data-testid="gov-cms-api-key-input"
              />
              <p className="seo-gov-hint" style={{ marginTop: '4px', fontSize: '11px' }}>
                For live WordPress: generate in WP Admin &gt; Users &gt; Profile &gt; Application
                Passwords (enter as <code>username:password</code>). Leave blank when testing with
                staging demo.
              </p>
            </div>
          </div>
        </div>

        <div className="seo-modal-footer">
          <button type="button" className="seo-btn seo-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="seo-btn seo-btn-primary" onClick={handleSave}>
            <i className={savedSuccess ? 'fas fa-check text-green' : 'fas fa-save'}></i>
            <span>{savedSuccess ? 'Saved Policy' : 'Save Governance Policy'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
